import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FilterViewListItem } from '@/shared/types/analytics';

interface SaveFilterFormProps {
  onSubmit: (data: { name: string; overwriteExisting: boolean }) => Promise<void>;
  existingViews: FilterViewListItem[];
  activeViewId?: string;
  onSuccess?: () => void;
  initialValue?: string;
  onCancel?: () => void;
  title?: string;
}

interface FormData {
  name: string;
}

export const SaveFilterForm = ({
  onSubmit,
  existingViews,
  activeViewId,
  onSuccess,
  initialValue,
  onCancel,
  title,
}: SaveFilterFormProps) => {
  const { t } = useTranslation();
  const displayTitle = title ?? t('brain_logs.save_filter_view');
  const [isSaving, setIsSaving] = useState(false);

  // Pre-fill with initial value if provided, otherwise use active view name if updating
  const activeView = existingViews.find((v) => v.id === activeViewId);
  const defaultName = initialValue || activeView?.name || '';

  const form = useForm<FormData>({ mode: 'onChange', defaultValues: { name: defaultName } });

  const handleSubmit = async (data: FormData) => {
    setIsSaving(true);
    try {
      // Always create a new filter view (overwriteExisting: false)
      // The "Update" functionality is handled separately by the parent component
      await onSubmit({ name: data.name, overwriteExisting: false });
      form.reset();
      onSuccess?.(); // Close the dropdown on success
    } catch (error) {
      console.error('Failed to save filter view:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    if (onCancel) {
      onCancel();
    } else {
      // Default behavior: call onSuccess to close the dropdown
      onSuccess?.();
    }
  };

  return (
    <div className="flex w-80 flex-col items-start justify-start gap-4 px-4 pt-4 pb-5">
      <p className="text-base leading-none font-medium">{displayTitle}</p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="w-full space-y-4">
          <FormField
            control={form.control}
            name="name"
            rules={{
              required: { value: true, message: t('validation.required') },
              maxLength: { value: 50, message: t('validation.max_length', { 0: 50 }) },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.name')}</FormLabel>
                <FormControl>
                  <Input maxLength={50} autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex w-full flex-row-reverse justify-start gap-2">
            <Button type="submit" size="md" isLoading={isSaving}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="ghost-neutral" size="md" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
